import type { ChatItem } from '../../../contracts/harness.types';
import {
  normalizeRefTags,
  stripRefTags,
  type RefTarget,
} from '../../../contracts/projects/ref-tags';

import {
  parseUserPreamble,
  prepareAssistantMarkdown,
  stripChatTags,
  userMessageRendersBubble,
} from '../components/projectChatMessage.helpers';

const isVisibleUserMessage = (item: ChatItem, isRoutineRunSeed: boolean): boolean => {
  if (item.kind !== 'user_message') return false;
  return userMessageRendersBubble(
    parseUserPreamble(item.text, { isRoutineRunSeed }),
    item.attachments?.length ?? 0
  );
};

/**
 * Concatenates settled assistant prose from the latest user-initiated turn.
 * Returns null when the turn has no copyable assistant text (e.g. tool-only).
 * `wholeTranscript` reads every item instead — an Evidence child run has no
 * user message to anchor a turn on. `labelFor` names a reference from the cache
 * its chip resolved through; the tag's hint is the fallback.
 *
 * Two text passes are the caller's: `stripCitationMarkup` removes Evidence's
 * cite and image markup, which Evidence owns, and `normalizeHeadingDepth` is
 * the answer kit's, which this headless zone may not import.
 */
export const extractLatestTurnCopyableText = (
  items: readonly ChatItem[],
  {
    wholeTranscript = false,
    labelFor,
    stripCitationMarkup,
    normalizeHeadingDepth,
  }: {
    wholeTranscript?: boolean;
    labelFor?: (target: RefTarget) => string | undefined;
    stripCitationMarkup: (text: string) => string;
    normalizeHeadingDepth: (markdown: string) => string;
  }
): string | null => {
  const firstUserMessageId = items.find((entry) => entry.kind === 'user_message')?.id;
  const latestTurnStart = wholeTranscript
    ? -1
    : items.findLastIndex((item) =>
        isVisibleUserMessage(item, item.id === firstUserMessageId)
      );
  if (!wholeTranscript && latestTurnStart < 0) return null;

  return items.slice(latestTurnStart + 1).reduce<string | null>((acc, item) => {
    // `text` can be absent at runtime despite the type — tolerate like the renderer does.
    if (item?.kind !== 'assistant_text' || item.streaming || !item.text) return acc;
    // Settled: strip cite/img/chat markup; keep chat titles as plain text. References
    // normalize before they strip, so a drifted tag still copies as the name.
    const withNames = stripRefTags(normalizeRefTags(item.text).text, labelFor);
    const text = prepareAssistantMarkdown(
      stripChatTags(stripCitationMarkup(withNames)),
      normalizeHeadingDepth
    )?.trim();
    if (!text) return acc;
    return acc === null ? text : `${acc}\n\n${text}`;
  }, null);
};
